"use client";

import { auth } from "@/firebase";
import {
  deleteFirestoreUserByUid,
  fetchFirestoreUser,
  saveFirestoreUser,
} from "@/utils/users";
import { FirestoreUser } from "@tunagari/shared/types/User";
import { useEffect, useState } from "react";
import useSWR from "swr";

const useFirestoreUser = () => {
  const [firebaseUID, setFirebaseUID] = useState<string | null>(null);

  // Track Firebase auth state changes to ensure SWR revalidates when auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setFirebaseUID(user?.uid || null);
    });

    return () => unsubscribe();
  }, []);

  const {
    data: { data: user } = {},
    error,
    mutate,
    isValidating,
  } = useSWR(
    firebaseUID,
    async (uid: string) => {
      const response = await fetchFirestoreUser(uid);
      if (!response.success) {
        throw new Error(response.error || "Failed to fetch firestore user");
      }
      return response;
    },
    {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    }
  );

  const save = async (data: Partial<FirestoreUser>) => {
    if (auth.currentUser) {
      const res = await saveFirestoreUser(auth.currentUser.uid, data);
      if (!res.success) {
        throw new Error(res.error);
      }
      mutate(); // 再フェッチして最新のデータを取得
    }
  };

  const destroy = async () => {
    if (auth.currentUser) {
      const res = await deleteFirestoreUserByUid(auth.currentUser.uid);
      if (!res.success) {
        throw new Error(res.error);
      }
      mutate(); // 再フェッチして最新のデータを取得
    }
  };

  return {
    user,
    save,
    destroy,
    refresh: mutate,
    isLoading: isValidating,
    isError: !!error,
  };
};

export default useFirestoreUser;
